/**
 * Solo Mode "Football Islands" — shared lives badge.
 *
 * Renders 5 heart icons (filled or outline) + a live MM:SS countdown
 * to the next regen. The countdown ticks every second in-place; the
 * authoritative `next_regen_in_seconds` is refetched from the server
 * whenever `livesData` changes (after a wrong answer, etc.).
 *
 * Used by both /solo (map) and /solo/level/:n.
 */
import { useEffect, useState } from "react";
import { Heart } from "lucide-react";

const MAX = 5;

function fmt(sec) {
  if (sec <= 0) return "--:--";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return `${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
}

export default function SoloLivesBadge({ lives = 0, nextRegenInSeconds = 0 }) {
  const [tick, setTick] = useState(nextRegenInSeconds);
  useEffect(() => {
    setTick(nextRegenInSeconds);
  }, [nextRegenInSeconds]);
  useEffect(() => {
    if (tick <= 0) return undefined;
    const id = setInterval(() => setTick((t) => Math.max(0, t - 1)), 1000);
    return () => clearInterval(id);
  }, [tick]);

  const full = Math.max(0, Math.min(MAX, lives));
  const isMax = full >= MAX;
  return (
    <div
      data-testid="solo-lives-badge"
      className="inline-flex items-center gap-3 px-4 py-2 rounded-full bg-black/40 border border-white/10 backdrop-blur"
    >
      <div className="flex items-center gap-1">
        {Array.from({ length: MAX }).map((_, i) => (
          <Heart
            key={i}
            size={18}
            strokeWidth={2}
            className={
              i < full
                ? "fill-rose-500 stroke-rose-500"
                : "fill-transparent stroke-zinc-600"
            }
          />
        ))}
      </div>
      <div className="text-xs tabular-nums text-zinc-400">
        {isMax ? (
          <span data-testid="solo-lives-max">Full</span>
        ) : (
          <span>
            Next life in{" "}
            <span data-testid="solo-lives-countdown" className="text-zinc-200 font-semibold">
              {fmt(tick)}
            </span>
          </span>
        )}
      </div>
    </div>
  );
}
